/**
 * Billing Component
 * Displays account balance, payment methods, and billing history
 */

import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import {
  CreditCard,
  Clock,
  DollarSign,
  TrendingUp,
  Calendar,
  AlertCircle,
  BarChart3,
  RefreshCw
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { billingAPI, userDashboardAPI } from '../services/api';
import { AuthContext } from '../context/AuthContext';
import {
  BalanceDisplay,
  PaymentMethodsList,
  AddPaymentMethodModal,
  TopUpModal,
  TransactionHistory,
  AutoRechargeSettings,
} from '../components/billing';

const BILLING_RATE_PER_MINUTE = 0.15;

const Billing = () => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.userlevel >= 200 || user?.isGroupOwner;

  // Balance state
  const [balance, setBalance] = useState(0);
  const [balanceFormatted, setBalanceFormatted] = useState('$0.00');
  const [autoRecharge, setAutoRecharge] = useState(false);
  const [autoRechargeThreshold, setAutoRechargeThreshold] = useState(500);
  const [autoRechargeAmount, setAutoRechargeAmount] = useState(2000);

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [setupIntentSecret, setSetupIntentSecret] = useState(null);

  // Modal state
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);

  // Loading states
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
  const [topUpLoading, setTopUpLoading] = useState(false);

  // Call usage state (existing functionality)
  const [calls, setCalls] = useState([]);
  const [useMockData, setUseMockData] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Fetch balance
  const fetchBalance = useCallback(async () => {
    try {
      const response = await billingAPI.getBalance();
      if (response.data.success) {
        setBalance(response.data.balance);
        setBalanceFormatted(response.data.balanceFormatted);
        setAutoRecharge(response.data.autoRecharge);
        setAutoRechargeThreshold(response.data.autoRechargeThreshold);
        setAutoRechargeAmount(response.data.autoRechargeAmount);
      }
    } catch (err) {
      console.warn('Failed to fetch balance:', err.message);
    } finally {
      setLoadingBalance(false);
    }
  }, []);

  // Fetch payment methods
  const fetchPaymentMethods = useCallback(async () => {
    try {
      const response = await billingAPI.getPaymentMethods();
      if (response.data.success) {
        setPaymentMethods(response.data.paymentMethods);
      }
    } catch (err) {
      console.warn('Failed to fetch payment methods:', err.message);
    } finally {
      setLoadingPaymentMethods(false);
    }
  }, []);

  // Fetch call usage data
  const fetchCalls = useCallback(async () => {
    try {
      const response = await userDashboardAPI.getCalls({ limit: 1000 });
      if (response.data.success) {
        setCalls(response.data.calls || []);
        setUseMockData(false);
      }
    } catch (err) {
      console.warn('API not available for calls:', err.message);
      setUseMockData(true);
    }
  }, []);

  useEffect(() => {
    fetchBalance();
    fetchPaymentMethods();
    fetchCalls();
  }, [fetchBalance, fetchPaymentMethods, fetchCalls]);

  // Handle add card
  const handleAddCard = async () => {
    try {
      const response = await billingAPI.createSetupIntent();
      if (response.data.success) {
        setSetupIntentSecret(response.data.clientSecret);
        setShowAddCardModal(true);
      }
    } catch (err) {
      console.error('Failed to create setup intent:', err);
      alert('Failed to initialize card setup. Please try again.');
    }
  };

  // Handle card added
  const handleCardAdded = async (paymentMethodId) => {
    try {
      await billingAPI.addPaymentMethod(paymentMethodId);
      fetchPaymentMethods();
    } catch (err) {
      console.error('Failed to save payment method:', err);
      alert('Failed to save payment method. Please try again.');
    }
  };

  // Handle remove card
  const handleRemoveCard = async (id) => {
    try {
      await billingAPI.removePaymentMethod(id);
      fetchPaymentMethods();
    } catch (err) {
      console.error('Failed to remove payment method:', err);
      alert('Failed to remove payment method. Please try again.');
    }
  };

  // Handle set default card
  const handleSetDefaultCard = async (id) => {
    try {
      await billingAPI.setDefaultPaymentMethod(id);
      fetchPaymentMethods();
    } catch (err) {
      console.error('Failed to set default payment method:', err);
      alert('Failed to set default payment method. Please try again.');
    }
  };

  // Handle top up
  const handleTopUp = async (amountCents, paymentMethodId) => {
    setTopUpLoading(true);
    try {
      const response = await billingAPI.topUp(amountCents, paymentMethodId);
      if (response.data.success) {
        setBalance(response.data.balanceAfter);
        setBalanceFormatted(response.data.balanceAfterFormatted);
        setShowTopUpModal(false);
      }
    } catch (err) {
      throw new Error(err.response?.data?.error || 'Failed to process payment');
    } finally {
      setTopUpLoading(false);
    }
  };

  // Handle save auto-recharge settings
  const handleSaveAutoRecharge = async (settings) => {
    try {
      const response = await billingAPI.updateSettings(settings);
      if (response.data.success) {
        setAutoRecharge(response.data.autoRecharge);
        setAutoRechargeThreshold(response.data.autoRechargeThreshold);
        setAutoRechargeAmount(response.data.autoRechargeAmount);
      }
    } catch (err) {
      console.error('Failed to update settings:', err);
      alert('Failed to save settings. Please try again.');
    }
  };

  // Calculate daily billing data
  const dailyBillingData = useMemo(() => {
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const dailyData = {};

    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      dailyData[dateKey] = {
        date: dateKey,
        day: day,
        callTimeMinutes: 0,
        billingAmount: 0,
        callCount: 0
      };
    }

    calls.forEach(call => {
      if (!call.createdAt || !call.duration) return;

      const callDate = new Date(call.createdAt);
      if (callDate.getMonth() !== selectedMonth || callDate.getFullYear() !== selectedYear) {
        return;
      }

      const dateKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(callDate.getDate()).padStart(2, '0')}`;
      if (dailyData[dateKey]) {
        const minutes = (call.duration || 0) / 60000;
        dailyData[dateKey].callTimeMinutes += minutes;
        dailyData[dateKey].billingAmount += minutes * BILLING_RATE_PER_MINUTE;
        dailyData[dateKey].callCount += 1;
      }
    });

    return Object.values(dailyData).map(day => ({
      ...day,
      dateLabel: `${day.day}`,
      callTimeFormatted: `${Math.floor(day.callTimeMinutes)}:${String(Math.floor((day.callTimeMinutes % 1) * 60)).padStart(2, '0')}`,
      billingFormatted: `$${day.billingAmount.toFixed(2)}`
    }));
  }, [calls, selectedMonth, selectedYear]);

  // Calculate monthly totals
  const monthlyTotals = useMemo(() => {
    const total = dailyBillingData.reduce((acc, day) => ({
      callTimeMinutes: acc.callTimeMinutes + day.callTimeMinutes,
      billingAmount: acc.billingAmount + day.billingAmount,
      callCount: acc.callCount + day.callCount
    }), { callTimeMinutes: 0, billingAmount: 0, callCount: 0 });

    return {
      ...total,
      callTimeFormatted: `${Math.floor(total.callTimeMinutes / 60)}h ${Math.floor(total.callTimeMinutes % 60)}m`,
      billingFormatted: `$${total.billingAmount.toFixed(2)}`
    };
  }, [dailyBillingData]);

  const getMonthName = (monthIndex) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return months[monthIndex];
  };

  const formatTooltip = (value, name) => {
    if (name === 'Call Time (min)') {
      return [`${Math.floor(value)}:${String(Math.floor((value % 1) * 60)).padStart(2, '0')}`, 'Call Time'];
    }
    if (name === 'Billing Amount') {
      return [`$${value.toFixed(2)}`, 'Billing'];
    }
    return [value, name];
  };

  const isLowBalance = balance < 500 && balance >= 0;

  return (
    <div className="h-full overflow-y-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-freshroad-black mb-2">Billing & Payments</h1>
        <p className="text-gray-600">Manage your account balance and payment methods</p>
      </div>

      {/* Balance and Payment Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Balance Display */}
        {!loadingBalance && (
          <BalanceDisplay
            balance={balance}
            balanceFormatted={balanceFormatted}
            onTopUp={() => setShowTopUpModal(true)}
            isLowBalance={isLowBalance}
          />
        )}

        {/* Payment Methods */}
        <PaymentMethodsList
          paymentMethods={paymentMethods}
          onAddNew={handleAddCard}
          onRemove={handleRemoveCard}
          onSetDefault={handleSetDefaultCard}
          isAdmin={isAdmin}
          loading={loadingPaymentMethods}
        />
      </div>

      {/* Auto-Recharge Settings */}
      <div className="mb-6">
        <AutoRechargeSettings
          enabled={autoRecharge}
          threshold={autoRechargeThreshold}
          amount={autoRechargeAmount}
          onSave={handleSaveAutoRecharge}
          isAdmin={isAdmin}
        />
      </div>

      {/* Transaction History */}
      <div className="mb-6">
        <TransactionHistory />
      </div>

      {/* Usage Stats Section */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-freshroad-black mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-freshroad-teal" />
          Call Usage
        </h2>

        {useMockData && (
          <div className="mb-4 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg inline-flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-600" />
            <span className="text-sm text-yellow-800">Using demo data (API not available)</span>
          </div>
        )}

        {/* Month/Year Selector */}
        <div className="mb-4 flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Month:</label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-freshroad-teal focus:border-transparent"
          >
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i} value={i}>{getMonthName(i)}</option>
            ))}
          </select>
          <label className="text-sm font-medium text-gray-700 ml-4">Year:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-freshroad-teal focus:border-transparent"
          >
            {Array.from({ length: 5 }, (_, i) => {
              const year = new Date().getFullYear() - 2 + i;
              return <option key={year} value={year}>{year}</option>;
            })}
          </select>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Call Time</p>
                <p className="text-3xl font-bold text-freshroad-black">{monthlyTotals.callTimeFormatted}</p>
                <p className="text-xs text-gray-500 mt-1">{monthlyTotals.callCount} calls</p>
              </div>
              <div className="p-3 bg-freshroad-gradient-teal rounded-lg">
                <Clock className="w-6 h-6 text-freshroad-teal" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Monthly Usage</p>
                <p className="text-3xl font-bold text-freshroad-black">{monthlyTotals.billingFormatted}</p>
                <p className="text-xs text-gray-500 mt-1">@ ${BILLING_RATE_PER_MINUTE.toFixed(2)}/min</p>
              </div>
              <div className="p-3 bg-freshroad-gradient-sky rounded-lg">
                <DollarSign className="w-6 h-6 text-freshroad-sky-blue" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Avg. Daily Usage</p>
                <p className="text-3xl font-bold text-freshroad-black">
                  ${(monthlyTotals.billingAmount / dailyBillingData.length).toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {Math.round(monthlyTotals.callTimeMinutes / dailyBillingData.length)} min/day
                </p>
              </div>
              <div className="p-3 bg-freshroad-gradient-blue rounded-lg">
                <TrendingUp className="w-6 h-6 text-freshroad-blue" />
              </div>
            </div>
          </div>
        </div>

        {/* Usage Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-freshroad-black mb-4">
            Daily Usage - {getMonthName(selectedMonth)} {selectedYear}
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyBillingData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="dateLabel" stroke="#6B7280" style={{ fontSize: '12px' }} />
              <YAxis yAxisId="left" stroke="#6B7280" style={{ fontSize: '12px' }} />
              <YAxis yAxisId="right" orientation="right" stroke="#6B7280" style={{ fontSize: '12px' }} />
              <Tooltip formatter={formatTooltip} />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="callTimeMinutes"
                name="Call Time (min)"
                stroke="#459EA3"
                strokeWidth={2}
                dot={{ fill: '#459EA3', r: 3 }}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="billingAmount"
                name="Billing Amount"
                stroke="#27AAE1"
                strokeWidth={2}
                dot={{ fill: '#27AAE1', r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Modals */}
      <AddPaymentMethodModal
        isOpen={showAddCardModal}
        onClose={() => setShowAddCardModal(false)}
        clientSecret={setupIntentSecret}
        onSuccess={handleCardAdded}
      />

      <TopUpModal
        isOpen={showTopUpModal}
        onClose={() => setShowTopUpModal(false)}
        paymentMethods={paymentMethods}
        onTopUp={handleTopUp}
        loading={topUpLoading}
      />
    </div>
  );
};

export default Billing;
